import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';
import { useAppThunkDispatch } from 'state/store';
import {
  selectCapacityFilters,
  upsertFiltersSettings,
} from 'state/Capacities/Filters/filtersSlice';
import { TeamsCapacityFilters } from 'types/store/teamsCapacity';

const DEFAULT_FILTERS: TeamsCapacityFilters = {
  status: [],
  archivedProjects: undefined,
  deactivatedUsers: undefined,
};

const useCapacityFilters = () => {
  const dispatch = useAppThunkDispatch();
  const appliedFilters = useSelector(selectCapacityFilters);
  const [draftFilters, setDraftFilters] =
    useState<TeamsCapacityFilters>(DEFAULT_FILTERS);
  const hasInitializedRef = useRef(false);

  const filtersCount = useMemo(() => {
    let count = draftFilters?.status?.length ?? 0;
    if (draftFilters?.deactivatedUsers === true) {
      count++;
    }
    if (draftFilters?.archivedProjects === true) {
      count++;
    }
    return count;
  }, [draftFilters]);

  const updateStatusFilter = useCallback(
    (values: string[]) =>
      setDraftFilters((prev) => ({
        ...prev,
        status: values,
      })),
    []
  );

  const updateCheckboxFilter = useCallback(
    (key: keyof TeamsCapacityFilters, value: boolean | undefined) => {
      setDraftFilters((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    []
  );

  const canApplyFilters = useMemo(
    () => !isEqual(draftFilters, appliedFilters ?? DEFAULT_FILTERS),
    [appliedFilters, draftFilters]
  );

  const applyFilters = useCallback(() => {
    dispatch(upsertFiltersSettings({ filters: draftFilters }));
  }, [dispatch, draftFilters]);

  const clearFilters = useCallback(() => {
    const emptyFilters = DEFAULT_FILTERS;
    setDraftFilters(emptyFilters);
    dispatch(upsertFiltersSettings({ filters: emptyFilters }));
  }, [dispatch]);

  useEffect(() => {
    if (appliedFilters && !hasInitializedRef.current) {
      setDraftFilters(appliedFilters);
      hasInitializedRef.current = true;
    }
  }, [appliedFilters]);

  // Sync draftFilters when external changes occur (e.g., user clears filters externally)
  useEffect(() => {
    if (appliedFilters && hasInitializedRef.current) {
      setDraftFilters(appliedFilters);
    }
  }, [appliedFilters]);

  return {
    draftFilters,
    filtersCount,
    updateStatusFilter,
    updateCheckboxFilter,
    canApplyFilters,
    applyFilters,
    clearFilters,
  };
};

export default useCapacityFilters;
